import { CAREER_ROUTES, PUBLIC_ADMIN_ROUTES } from '@/constants';
import { axiosBaseQuery } from '@/services/axios-base-query';
import type {
  PublicAdminAssignPlanPayload,
  PublicAdminAvailableProject,
  PublicAdminClient,
  PublicAdminClientScopeOption,
  PublicAdminClientDetails,
  PublicAdminClientTemplate,
  PublicAdminClientUser,
  PublicAdminFeature,
  PublicAdminFeaturePayload,
  PublicAdminPaymentPlan,
  PublicAdminPricingPlanPayload,
  PublicAdminPricingModule,
  PublicAdminPricingModulePayload,
  PublicAdminPricingPlan,
  PublicAdminCreateSkillPayload,
  PublicAdminScope,
  PublicAdminScopePayload,
  PublicAdminSkill,
  PublicAdminUpdateSkillPayload,
} from '@/types';

import { createApi } from '@reduxjs/toolkit/query/react';

export type CareerSummary = {
  skills: number;
  courses: number;
  certifications: number;
  experiences: number;
  education: number;
  unapplied_jobs: number;
  internal_school_projects: number;
  external_projects: number;
}

export type PublicCareer = {
  id: number;
  career_type: number | null;
  creator_schema: string;
  title: string;
  description: string;
  location: string;
  salary: string;
  experience: string | null;
  education: string | null;
  responsibilities: string | null;
  qualifications: string | null;
  benefits: string | null;
  application_process: string | null;
  start_date: string;
  end_date: string;
  created_on: string;
  updated_on: string;
  is_active: boolean;
  is_published: boolean;
  has_applied?: boolean;
  created_by?: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    photo_url?: string | null;
  };
  skills: Array<{ id?: number; name?: string }>;
  company_details?: {
    id: number;
    name: string;
    logo?: string | null;
    website?: string | null;
    address?: string | null;
    telephone?: string | null;
    email?: string | null;
    about_us?: string | null;
    slogan?: string | null;
    primary_color?: string | null;
    banner?: string | null;
  };
  received_applications: Array<{
    id: number;
    applicant: {
      id: string;
      email: string;
      first_name: string;
      last_name: string;
      photo_url?: string | null;
    };
    status: string;
    cover_letter: string;
    resume_url: string | null;
    is_shortlisted: boolean;
    is_accepted: boolean;
    is_rejected: boolean;
    is_interviewed: boolean;
    is_hired: boolean;
    created_on: string;
    updated_on: string;
  }>;
};


export const publicAdminApi = createApi({
  reducerPath: 'PublicAdminApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['PublicAdminPlans', 'PublicAdminModules', 'PublicAdminFeatures', 'PublicAdminClients', 'PublicAdminSkills', 'PublicAdminScopes', 'PublicCareers'],
  endpoints: (builder) => ({
    getPublicAdminPricingPlans: builder.query<PublicAdminPricingPlan[], void>({
      query: () => ({
        url: PUBLIC_ADMIN_ROUTES.GET_PRICING_PLANS,
        method: 'GET',
      }),
      providesTags: ['PublicAdminPlans'],
    }),
    createPublicAdminPricingPlan: builder.mutation<
      PublicAdminPricingPlan,
      PublicAdminPricingPlanPayload
    >({
      query: (data) => ({
        url: PUBLIC_ADMIN_ROUTES.GET_PRICING_PLANS,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['PublicAdminPlans'],
    }),
    updatePublicAdminPricingPlan: builder.mutation<
      PublicAdminPricingPlan,
      { planId: number | string; data: PublicAdminPricingPlanPayload }
    >({
      query: ({ planId, data }) => ({
        url: PUBLIC_ADMIN_ROUTES.PRICING_PLAN_DETAIL(planId),
        method: 'PATCH',
        data,
      }),
      invalidatesTags: ['PublicAdminPlans'],
    }),
    deletePublicAdminPricingPlan: builder.mutation<void, { planId: number | string }>(
      {
        query: ({ planId }) => ({
          url: PUBLIC_ADMIN_ROUTES.PRICING_PLAN_DETAIL(planId),
          method: 'DELETE',
        }),
        invalidatesTags: ['PublicAdminPlans'],
      }
    ),
    getPublicAdminPaymentPlans: builder.query<PublicAdminPaymentPlan[], void>({
      query: () => ({
        url: PUBLIC_ADMIN_ROUTES.GET_PAYMENT_PLANS,
        method: 'GET',
      }),
      providesTags: ['PublicAdminPlans'],
    }),
    createPublicAdminPaymentPlan: builder.mutation<
      PublicAdminPaymentPlan,
      PublicAdminAssignPlanPayload
    >({
      query: (data) => ({
        url: PUBLIC_ADMIN_ROUTES.GET_PAYMENT_PLANS,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['PublicAdminPlans', 'PublicAdminClients'],
    }),
    getPublicAdminClients: builder.query<PublicAdminClient[], void>({
      query: () => ({
        url: PUBLIC_ADMIN_ROUTES.GET_ALL_CLIENTS,
        method: 'GET',
      }),
      providesTags: ['PublicAdminClients'],
    }),
    getPublicAdminAvailableProjects: builder.query<PublicAdminAvailableProject[], void>({
      query: () => ({
        url: PUBLIC_ADMIN_ROUTES.GET_AVAILABLE_PROJECTS,
        method: 'GET',
      }),
    }),
    getPublicAdminClientScopeOptions: builder.query<
      PublicAdminClientScopeOption[],
      void
    >({
      query: () => ({
        url: PUBLIC_ADMIN_ROUTES.GET_CLIENT_SCOPE_OPTIONS,
        method: 'GET',
      }),
    }),
    getPublicAdminAvailableProjectDetail: builder.query<
      PublicAdminAvailableProject,
      number | string
    >({
      query: (projectId) => ({
        url: PUBLIC_ADMIN_ROUTES.AVAILABLE_PROJECT_DETAIL(projectId),
        method: 'GET',
      }),
    }),
    assignPublicAdminProject: builder.mutation<
      PublicAdminAvailableProject,
      { projectId: number | string; data: { published: boolean } }
    >({
      query: ({ projectId, data }) => ({
        url: PUBLIC_ADMIN_ROUTES.ASSIGN_PROJECT_DETAIL(projectId),
        method: 'PATCH',
        data,
      }),
    }),
    getPublicAdminClientTemplate: builder.query<
      PublicAdminClientTemplate,
      number | string
    >({
      query: (clientId) => ({
        url: PUBLIC_ADMIN_ROUTES.CLIENT_TEMPLATE(clientId),
        method: 'GET',
      }),
    }),
    getPublicAdminClientDetails: builder.query<
      PublicAdminClientDetails,
      number | string
    >({
      query: (clientId) => ({
        url: PUBLIC_ADMIN_ROUTES.CLIENT_DETAILS(clientId),
        method: 'GET',
      }),
    }),
    getPublicAdminClientUsers: builder.query<PublicAdminClientUser[], number | string>({
      query: (clientId) => ({
        url: PUBLIC_ADMIN_ROUTES.CLIENT_USERS(clientId),
        method: 'GET',
      }),
    }),
    deletePublicAdminClientUser: builder.mutation<
      void,
      { clientId: number | string; userId: string }
    >({
      query: ({ clientId, userId }) => ({
        url: PUBLIC_ADMIN_ROUTES.CLIENT_USER_DELETE(clientId, userId),
        method: 'DELETE',
      }),
    }),
    getPublicAdminPricingModules: builder.query<PublicAdminPricingModule[], void>({
      query: () => ({
        url: PUBLIC_ADMIN_ROUTES.GET_PRICING_MODULES,
        method: 'GET',
      }),
      providesTags: ['PublicAdminModules'],
    }),
    createPublicAdminPricingModule: builder.mutation<
      PublicAdminPricingModule,
      PublicAdminPricingModulePayload
    >({
      query: (data) => ({
        url: PUBLIC_ADMIN_ROUTES.GET_PRICING_MODULES,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['PublicAdminModules'],
    }),
    updatePublicAdminPricingModule: builder.mutation<
      PublicAdminPricingModule,
      { moduleId: number | string; data: PublicAdminPricingModulePayload }
    >({
      query: ({ moduleId, data }) => ({
        url: PUBLIC_ADMIN_ROUTES.PRICING_MODULE_DETAIL(moduleId),
        method: 'PATCH',
        data,
      }),
      invalidatesTags: ['PublicAdminModules'],
    }),
    deletePublicAdminPricingModule: builder.mutation<
      void,
      { moduleId: number | string }
    >({
      query: ({ moduleId }) => ({
        url: PUBLIC_ADMIN_ROUTES.PRICING_MODULE_DETAIL(moduleId),
        method: 'DELETE',
      }),
      invalidatesTags: ['PublicAdminModules'],
    }),
    getPublicAdminFeatures: builder.query<PublicAdminFeature[], void>({
      query: () => ({
        url: PUBLIC_ADMIN_ROUTES.GET_FEATURES,
        method: 'GET',
      }),
      providesTags: ['PublicAdminFeatures'],
    }),
    createPublicAdminFeature: builder.mutation<
      PublicAdminFeature,
      PublicAdminFeaturePayload
    >({
      query: (data) => ({
        url: PUBLIC_ADMIN_ROUTES.GET_FEATURES,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['PublicAdminFeatures'],
    }),
    updatePublicAdminFeature: builder.mutation<
      PublicAdminFeature,
      { featureId: number | string; data: PublicAdminFeaturePayload }
    >({
      query: ({ featureId, data }) => ({
        url: PUBLIC_ADMIN_ROUTES.FEATURE_DETAIL(featureId),
        method: 'PATCH',
        data,
      }),
      invalidatesTags: ['PublicAdminFeatures'],
    }),
    deletePublicAdminFeature: builder.mutation<void, { featureId: number | string }>(
      {
        query: ({ featureId }) => ({
          url: PUBLIC_ADMIN_ROUTES.FEATURE_DETAIL(featureId),
          method: 'DELETE',
        }),
        invalidatesTags: ['PublicAdminFeatures'],
      }
    ),
    createCareer: builder.mutation<
      unknown,
      {
        tenant: string;
        data: {
          career_type: string | null;
          title: string;
          description: string;
          location: string;
          salary: string;
          experience: string | null;
          education: string | null;
          responsibilities: string | null;
          qualifications: string | null;
          benefits: string | null;
          application_process: string | null;
          start_date: string;
          end_date: string;
          is_active: boolean;
          is_published: boolean;
          skills: number[];
        };
      }
    >({
      query: ({ tenant, data }) => ({
        url: CAREER_ROUTES.CAREERS(tenant),
        method: 'POST',
        data,
      }),
    }),
    getCareerSummmary: builder.query<CareerSummary, { tenant: string }>({
      query: ({ tenant }) => ({
        url: CAREER_ROUTES.CAREER_SUMMARY(tenant),
        method: 'GET',
      }),
      providesTags: ['PublicCareers'],
    }),
    getCareers: builder.query<PublicCareer[], void>({
      query: () => ({
        url: CAREER_ROUTES.PUBLIC_CAREERS,
        method: 'GET',
      }),
      providesTags: ['PublicCareers'],
    }),
    getCareerById: builder.query<PublicCareer, number | string>({
      query: (id) => ({
        url: CAREER_ROUTES.PUBLIC_CAREER_DETAIL(id),
        method: 'GET',
      }),
      providesTags: ['PublicCareers'],
    }),
    updateCareer: builder.mutation<
      PublicCareer,
      { tenant: string; id: number | string; data: FormData }
    >({
      query: ({ tenant, id, data }) => ({
        url: CAREER_ROUTES.CAREERS(tenant) + `${id}/`,
        method: 'PUT',
        data,
      }),
      async onQueryStarted({ id, data }, { dispatch, queryFulfilled }) {
        const nextValues: Partial<PublicCareer> = {};
        data.forEach((value, key) => {
          if (key === 'is_active' || key === 'is_published') {
            nextValues[key] = value === 'true';
            return;
          }
          if (key === 'career_type') {
            nextValues.career_type = value ? Number(value) : null;
            return;
          }
          if (key === 'skills') {
            try {
              nextValues.skills = JSON.parse(String(value));
            } catch {
              nextValues.skills = [];
            }
            return;
          }
          (nextValues as Record<string, unknown>)[key] = value === '' ? null : value;
        });

        const patchById = dispatch(
          publicAdminApi.util.updateQueryData('getCareerById', id, (draft) => {
            Object.assign(draft, nextValues);
          })
        );
        const patchList = dispatch(
          publicAdminApi.util.updateQueryData('getCareers', undefined, (draft) => {
            const target = draft.find((item) => item.id === Number(id));
            if (target) Object.assign(target, nextValues);
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchById.undo();
          patchList.undo();
        }
      },
    }),
    deleteCareer: builder.mutation<void, number | string>({
      query: (id) => ({
        url: CAREER_ROUTES.PUBLIC_CAREER_DETAIL(id),
        method: 'DELETE',
      }),
      invalidatesTags: ['PublicCareers'],
    }),
    createCareerApplication: builder.mutation<unknown, FormData>({
      query: (data) => ({
        url: CAREER_ROUTES.CAREER_APPLICATIONS,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['PublicCareers'],
    }),
    getPublicAdminSkills: builder.query<PublicAdminSkill[], void>({
      query: () => ({
        url: PUBLIC_ADMIN_ROUTES.GET_SKILLS,
        method: 'GET',
      }),
      providesTags: ['PublicAdminSkills'],
    }),
    createPublicAdminSkill: builder.mutation<
      PublicAdminSkill,
      PublicAdminCreateSkillPayload
    >({
      query: (data) => ({
        url: PUBLIC_ADMIN_ROUTES.CREATE_SKILL,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['PublicAdminSkills'],
    }),
    updatePublicAdminSkill: builder.mutation<
      PublicAdminSkill,
      { skillId: number | string; data: PublicAdminUpdateSkillPayload }
    >({
      query: ({ skillId, data }) => ({
        url: PUBLIC_ADMIN_ROUTES.SKILL_DETAIL(skillId),
        method: 'PATCH',
        data,
      }),
      invalidatesTags: ['PublicAdminSkills'],
    }),
    deletePublicAdminSkill: builder.mutation<void, { skillId: number | string }>(
      {
        query: ({ skillId }) => ({
          url: PUBLIC_ADMIN_ROUTES.DELETE_SKILL(skillId),
          method: 'DELETE',
        }),
        invalidatesTags: ['PublicAdminSkills'],
      }
    ),
    bulkAddPublicAdminSkills: builder.mutation<unknown, { data: FormData }>({
      query: ({ data }) => ({
        url: PUBLIC_ADMIN_ROUTES.BULK_ADD_SKILLS,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['PublicAdminSkills'],
    }),
    getPublicAdminScopes: builder.query<PublicAdminScope[], void>({
      query: () => ({
        url: PUBLIC_ADMIN_ROUTES.GET_SCOPES,
        method: 'GET',
      }),
      providesTags: ['PublicAdminScopes'],
    }),
    createPublicAdminScope: builder.mutation<
      PublicAdminScope,
      PublicAdminScopePayload
    >({
      query: (data) => ({
        url: PUBLIC_ADMIN_ROUTES.GET_SCOPES,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['PublicAdminScopes'],
    }),
    updatePublicAdminScope: builder.mutation<
      PublicAdminScope,
      { scopeId: number | string; data: PublicAdminScopePayload }
    >({
      query: ({ scopeId, data }) => ({
        url: PUBLIC_ADMIN_ROUTES.SCOPE_DETAIL(scopeId),
        method: 'PATCH',
        data,
      }),
      invalidatesTags: ['PublicAdminScopes'],
    }),
    deletePublicAdminScope: builder.mutation<void, { scopeId: number | string }>({
      query: ({ scopeId }) => ({
        url: PUBLIC_ADMIN_ROUTES.SCOPE_DETAIL(scopeId),
        method: 'DELETE',
      }),
      invalidatesTags: ['PublicAdminScopes'],
    }),
  }),
});

export const {
  useCreateCareerMutation,
  useGetCareerSummmaryQuery,
  useGetCareersQuery,
  useGetCareerByIdQuery,
  useUpdateCareerMutation,
  useDeleteCareerMutation,
  useCreateCareerApplicationMutation,
  useGetPublicAdminPricingPlansQuery,
  useCreatePublicAdminPricingPlanMutation,
  useUpdatePublicAdminPricingPlanMutation,
  useDeletePublicAdminPricingPlanMutation,
  useGetPublicAdminPaymentPlansQuery,
  useCreatePublicAdminPaymentPlanMutation,
  useGetPublicAdminClientsQuery,
  useGetPublicAdminAvailableProjectsQuery,
  useGetPublicAdminClientScopeOptionsQuery,
  useGetPublicAdminAvailableProjectDetailQuery,
  useAssignPublicAdminProjectMutation,
  useGetPublicAdminClientTemplateQuery,
  useGetPublicAdminClientDetailsQuery,
  useGetPublicAdminClientUsersQuery,
  useDeletePublicAdminClientUserMutation,
  useGetPublicAdminPricingModulesQuery,
  useCreatePublicAdminPricingModuleMutation,
  useUpdatePublicAdminPricingModuleMutation,
  useDeletePublicAdminPricingModuleMutation,
  useGetPublicAdminFeaturesQuery,
  useCreatePublicAdminFeatureMutation,
  useUpdatePublicAdminFeatureMutation,
  useDeletePublicAdminFeatureMutation,
  useGetPublicAdminSkillsQuery,
  useCreatePublicAdminSkillMutation,
  useUpdatePublicAdminSkillMutation,
  useDeletePublicAdminSkillMutation,
  useBulkAddPublicAdminSkillsMutation,
  useGetPublicAdminScopesQuery,
  useCreatePublicAdminScopeMutation,
  useUpdatePublicAdminScopeMutation,
  useDeletePublicAdminScopeMutation,
} = publicAdminApi;
